import { computed, DestroyRef, inject, Injectable, signal, untracked } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subject, switchMap, catchError, defer, EMPTY, map, finalize } from 'rxjs';
import { WithdrawalService } from '../services/withdrawal.service';
import { AddTokenService } from '../services/add-token.service';
import { TransactionStatusService } from '../services/transaction-status.service';
import { WalletDialogFlow } from './wallet-dialog-flow';
import type {
  WithdrawalAuthTxnResult,
  WithdrawalDialogStep,
  WithdrawalSuccessState,
  TopupCategory,
  TopupMethod,
  TopupToken,
  InitWithdrawalParams,
} from '../models';
import type { PostTxStatus } from '../models/topup.model';
import type { AddTokenFormValue } from '../models/add-token.model';
import { PinAttemptStore } from './pin-attempt.store';

/**
 * Store for the withdrawal dialog ("Retirer").
 *
 * Scoped to the dialog — provided in WithdrawalDialogComponent.providers, so a fresh
 * instance is created each time the dialog opens and destroyed when it closes.
 *
 * The dialog has three steps:
 *   'form'    — User picks an amount, payment method, account, and optional category/note.
 *               Each selection progressively reveals the next section.
 *   'pin'     — User confirms with their 4-digit PIN. Up to (N) attempts before lockout.
 *   'failure' — Shown when the transaction itself fails after PIN is accepted.
 *               The user can go back and modify, or retry with the same PIN.
 *
 * Unlike the topup flow, withdrawal always requires PIN (no is_pin_required check),
 * and the submit is two-stage: verify_pin → init_psp_withdraw.
 *
 * On success, `lastWithdrawalResult` is set. The dialog component watches this
 * signal, emits the payload to WalletPage, and closes itself.
 */
@Injectable()
export class WithdrawalStore {
  private readonly withdrawalService = inject(WithdrawalService);
  private readonly addTokenService = inject(AddTokenService);
  private readonly transactionStatus = inject(TransactionStatusService);
  private readonly pinStore = inject(PinAttemptStore);
  private readonly destroyRef = inject(DestroyRef);
  private readonly flow = new WalletDialogFlow(this.destroyRef);

  // ── Mutable private state ─────────────────────────────────────────────────

  // Form state (same shape as TopupStore)
  private readonly _amount = signal<number | null>(null);
  private readonly _selectedMethod = signal<TopupMethod | null>(null);
  private readonly _selectedToken = signal<TopupToken | null>(null);
  private readonly _categoryId = signal<string>('');
  private readonly _message = signal<string>('');
  private readonly _dialogStep = signal<WithdrawalDialogStep>('form');
  private readonly _addAccountMode = signal(false);

  // Loading states
  private readonly _methodsLoading = signal(false);
  private readonly _tokensLoading = signal(false);
  private readonly _authTxnLoading = signal(false);
  private readonly _verifyPinLoading = signal(false);
  private readonly _submitting = signal(false);
  private readonly _addTokenLoading = signal(false);

  // Error states
  private readonly _addTokenError = signal<string | null>(null);
  private readonly _tokensError = signal<string | null>(null);
  private readonly _error = signal<string | null>(null);
  /** Human-readable error message from a failed init_psp_withdraw */
  private readonly _failureError = signal<string | null>(null);
  /** transaction_id from a failed response — shown as support reference in the failure UI */
  private readonly _failureTransactionId = signal<string | null>(null);
  /** error_code from a failed response (e.g. "311") — shown alongside the message */
  private readonly _failureErrorCode = signal<string | null>(null);

  // List data
  private readonly _methods = signal<TopupMethod[]>([]);
  private readonly _tokens = signal<TopupToken[]>([]);
  private readonly _categories = signal<TopupCategory[]>([]);
  private readonly _authTxnResult = signal<WithdrawalAuthTxnResult | null>(null);
  private readonly _lastWithdrawalResult = signal<WithdrawalSuccessState | null>(null);
  private readonly _txDetailLoading = signal(false);
  private readonly _txStatus = signal<PostTxStatus | null>(null);

  // Withdrawal-specific state
  /** Wallet balance passed in from WalletPage — used for real-time balance guard */
  private readonly _walletBalance = signal<number>(0);
  /** withdrawal_id from auth_txn response — required by init_psp_withdraw */
  private readonly _withdrawalId = signal<string | null>(null);
  /** The raw PIN entered by the user — used as authentication_data in init_psp_withdraw */
  private readonly _authData = signal<string | null>(null);

  // PIN attempt / lockout state is owned by the dialog-scoped PinAttemptStore.

  // ── Public readonly state ─────────────────────────────────────────────────

  readonly amount = this._amount.asReadonly();
  readonly selectedMethod = this._selectedMethod.asReadonly();
  readonly selectedToken = this._selectedToken.asReadonly();
  readonly categoryId = this._categoryId.asReadonly();
  readonly message = this._message.asReadonly();
  readonly dialogStep = this._dialogStep.asReadonly();
  readonly addAccountMode = this._addAccountMode.asReadonly();
  readonly walletBalance = this._walletBalance.asReadonly();

  readonly methodsLoading = this._methodsLoading.asReadonly();
  readonly tokensLoading = this._tokensLoading.asReadonly();
  readonly authTxnLoading = this._authTxnLoading.asReadonly();
  readonly verifyPinLoading = this._verifyPinLoading.asReadonly();
  readonly submitting = this._submitting.asReadonly();
  readonly addTokenLoading = this._addTokenLoading.asReadonly();

  readonly addTokenError = this._addTokenError.asReadonly();
  readonly tokensError = this._tokensError.asReadonly();
  readonly error = this._error.asReadonly();
  readonly failureError = this._failureError.asReadonly();
  readonly failureTransactionId = this._failureTransactionId.asReadonly();
  readonly failureErrorCode = this._failureErrorCode.asReadonly();

  readonly methods = this._methods.asReadonly();
  readonly tokens = this._tokens.asReadonly();
  readonly categories = this._categories.asReadonly();
  readonly authTxnResult = this._authTxnResult.asReadonly();
  /** Set on successful withdrawal — drives the WithdrawalSuccessBannerComponent in WalletPage */
  readonly lastWithdrawalResult = this._lastWithdrawalResult.asReadonly();
  readonly txDetailLoading = this._txDetailLoading.asReadonly();
  readonly txStatus = this._txStatus.asReadonly();

  readonly pinAttempts = this.pinStore.pinAttempts;
  readonly pinError = this.pinStore.pinError;
  readonly pinLocked = this.pinStore.pinLocked;

  // ── Computed ──────────────────────────────────────────────────────────────

  readonly isAmountValid = computed(() => {
    const a = this._amount();
    return a !== null && a > 0;
  });

  /** True when the entered amount exceeds the available wallet balance */
  readonly amountExceedsBalance = computed(() => (this._amount() ?? 0) > this._walletBalance());

  /**
   * Inline validation error for the amount field.
   * "Solde insuffisant" replaces the standard balance display when amount > balance.
   */
  readonly amountError = computed((): string | null => {
    const a = this._amount();
    if (a === null || a === 0) return null;
    if (this.amountExceedsBalance()) return 'Solde insuffisant';
    return null;
  });

  // Methods are only shown when the amount is positive and within the available balance.
  // No point showing payment options the user can't use.
  readonly showMethods = computed(() => {
    const a = this._amount();
    return a !== null && a > 0 && !this.amountExceedsBalance();
  });

  /** Progressive reveal: show token/account section once a method is selected */
  readonly showTokens = computed(() => this._selectedMethod() !== null);

  /** Progressive reveal: show category + message once a token is selected */
  readonly showDetails = computed(() => this._selectedToken() !== null);

  // All fields must be filled and auth_txn must have resolved before the user can proceed.
  readonly canContinue = computed(
    () =>
      this.isAmountValid() &&
      !this.amountExceedsBalance() &&
      this._selectedMethod() !== null &&
      this._selectedToken() !== null &&
      !this._authTxnLoading(),
  );

  // Card methods use withdrawal_required_params (pan/card_number) instead of a phone number.
  readonly isCardMethod = computed(() => {
    const method = this._selectedMethod();
    if (!method) return false;
    if (method.processor) {
      return method.processor.withdrawal_required_params.some(
        (p) => p.name === 'card_number' || p.name === 'pan',
      );
    }
    return method.name === 'Carte';
  });

  /** Total amount the wallet will be debited: base amount + fee debtor */
  readonly totalWithFees = computed(() => {
    const base = this._amount() ?? 0;
    const fee = this._authTxnResult()?.fee.debtor ?? this._selectedMethod()?.fee ?? 0;
    return base + fee;
  });

  /** Currently selected category object */
  readonly selectedCategory = computed(
    () => this._categories().find((c) => c.id === this._categoryId()) ?? null,
  );

  /**
   * Human-readable PIN error shown below the dot indicators.
   * Delegated to {@link PinAttemptStore}.
   */
  readonly pinErrorMessage = this.pinStore.pinErrorMessage;

  // ── Subjects (cancellable pipelines) ──────────────────────────────────────

  private readonly methodsTrigger$ = new Subject<number>();
  private readonly tokensTrigger$ = new Subject<string>();
  private readonly authTxnTrigger$ = new Subject<{
    amount: number;
    processorId: string;
    tokenId: string;
    methodId: string;
  }>();
  private readonly verifyPinTrigger$ = new Subject<{
    rawPin: string;
    withdrawalId: string;
  }>();
  private readonly submitTrigger$ = new Subject<InitWithdrawalParams>();
  private readonly addTokenTrigger$ = new Subject<AddTokenFormValue>();
  private readonly _detailTrigger$ = new Subject<string>();

  constructor() {
    this.wireMethodsPipeline();
    this.wireTokensPipeline();
    this.wireAuthTxnPipeline();
    this.wireVerifyPinPipeline();
    this.wireSubmitPipeline();
    this.wireAddTokenPipeline();
    this.wireDetailPipeline();
  }

  // ── Actions ───────────────────────────────────────────────────────────────

  // Called by the parent page when the dialog opens so balance validation works correctly.
  setWalletBalance(balance: number): void {
    this._walletBalance.set(balance);
  }

  setAmount(value: number | null): void {
    this._amount.set(value);
    // Reset downstream selections when amount changes
    this._selectedMethod.set(null);
    this._selectedToken.set(null);
    this._methods.set([]);
    this._tokens.set([]);
    this._addAccountMode.set(false);
    this._authTxnResult.set(null);
    this._categories.set([]);
    this._withdrawalId.set(null);
    this._addTokenLoading.set(false);
    this._addTokenError.set(null);
    this._error.set(null);

    // Only fetch methods when amount is valid AND within the available balance.
    // If the amount exceeds the balance, no API call is made and methods stay hidden.
    if (value !== null && value > 0 && !this.amountExceedsBalance()) {
      this._methodsLoading.set(true);
      this.methodsTrigger$.next(value);
    }
  }

  selectMethod(method: TopupMethod): void {
    if (this._selectedMethod()?.id === method.id) return;
    this._selectedMethod.set(method);
    this._selectedToken.set(null);
    this._addAccountMode.set(false);
    this._authTxnResult.set(null);
    this._categories.set([]);
    this._withdrawalId.set(null);
    this._addTokenLoading.set(false);
    this._addTokenError.set(null);
    this._tokensError.set(null);
    this._error.set(null);

    if (method.processor) {
      this._tokensLoading.set(true);
      this._tokens.set([]);
      this.tokensTrigger$.next(method.processor._id);
    } else {
      // Card methods may have no processor — tokens cannot be fetched without
      // a processor ID. The user must add a card manually via the add-account form.
      this._tokens.set([]);
      this._tokensLoading.set(false);
    }
  }

  retryTokens(): void {
    const method = this._selectedMethod();
    if (!method?.processor?._id) return;
    this._tokensError.set(null);
    this._tokensLoading.set(true);
    this._tokens.set([]);
    this.tokensTrigger$.next(method.processor._id);
  }

  selectToken(token: TopupToken): void {
    this._selectedToken.set(token);
    this._addAccountMode.set(false);
    this._error.set(null);

    const method = this._selectedMethod();
    const amount = this._amount();
    if (!method?.processor || amount === null) return;

    this._authTxnLoading.set(true);
    this._authTxnResult.set(null);
    this._withdrawalId.set(null);
    this.authTxnTrigger$.next({
      amount,
      processorId: method.processor._id,
      tokenId: token._id,
      methodId: method.id,
    });
  }

  toggleAddAccount(): void {
    this._addAccountMode.update((v) => !v);
    this._addTokenError.set(null);
  }

  addToken(form: AddTokenFormValue): void {
    if (this._addTokenLoading()) return;
    this._addTokenLoading.set(true);
    this._addTokenError.set(null);
    this.addTokenTrigger$.next(form);
  }

  setCategoryId(id: string): void {
    this._categoryId.set(id);
  }

  setMessage(msg: string): void {
    this._message.set(msg);
  }

  // Withdrawal always requires PIN — skip any is_pin_required check and go straight to the keypad.
  continuer(): void {
    if (!this.canContinue()) return;
    this._dialogStep.set('pin');
  }

  /**
   * Called when the user finishes entering their 4-digit PIN.
   *
   * Kicks off the two-stage submit: verify_pin first, then init_psp_withdraw on success.
   * The withdrawal ID from auth_txn is required — if it's missing something went wrong
   * earlier in the flow and we bail out silently rather than firing a broken request.
   */
  onPinEntered(rawPin: string): void {
    if (this.pinStore.pinLocked() || this._verifyPinLoading()) return;
    if (rawPin.length !== 4) return;

    const withdrawalId = this._withdrawalId();
    if (!withdrawalId) return;

    this._verifyPinLoading.set(true);
    this.pinStore.clearError();
    this.verifyPinTrigger$.next({ rawPin, withdrawalId });
  }

  // Go back to the form. Clear PIN state so the keypad starts clean next time.
  backToForm(): void {
    this._dialogStep.set('form');
    this.pinStore.reset();
    this._authData.set(null);
  }

  /**
   * "Réessayer" from the failure screen.
   *
   * Sends the user back to the PIN step for another attempt.
   * PIN lockout is reset here because verify_pin already succeeded — the failure
   * happened during init_psp_withdraw, not during PIN validation.
   */
  retryFromFailure(): void {
    this._dialogStep.set('pin');
    this._failureError.set(null);
    this._failureTransactionId.set(null);
    this._failureErrorCode.set(null);
    this._submitting.set(false);
    this._authData.set(null);
    this.pinStore.reset();
  }

  /** Reset all state — call on dialog close (via p-dialog's onHide) */
  reset(): void {
    this._amount.set(null);
    this._selectedMethod.set(null);
    this._selectedToken.set(null);
    this._categoryId.set('');
    this._message.set('');
    this._dialogStep.set('form');
    this._addAccountMode.set(false);
    this._methodsLoading.set(false);
    this._tokensLoading.set(false);
    this._authTxnLoading.set(false);
    this._verifyPinLoading.set(false);
    this._submitting.set(false);
    this._addTokenLoading.set(false);
    this._addTokenError.set(null);
    this._tokensError.set(null);
    this._error.set(null);
    this._failureError.set(null);
    this._failureTransactionId.set(null);
    this._failureErrorCode.set(null);
    this._methods.set([]);
    this._tokens.set([]);
    this._categories.set([]);
    this._authTxnResult.set(null);
    this._lastWithdrawalResult.set(null);
    this._withdrawalId.set(null);
    this._authData.set(null);
    this.pinStore.reset();
  }

  // ── Private helpers ───────────────────────────────────────────────────────

  /**
   * Triggers init_psp_withdraw after a successful verify_pin.
   *
   * All inputs should be set by this point — the early return is just a safety net
   * in case something unexpected happened earlier in the flow.
   */
  private dispatchSubmit(): void {
    const method = this._selectedMethod();
    const token = this._selectedToken();
    const amount = this._amount();
    const authTxn = this._authTxnResult();
    const authData = this._authData();
    const withdrawalId = this._withdrawalId();

    if (!method?.processor || !token || amount === null || !authTxn || !authData || !withdrawalId) {
      return;
    }

    this._submitting.set(true);
    this.submitTrigger$.next({
      amount,
      processorId: method.processor._id,
      tokenId: token._id,
      categoryId: this._categoryId(),
      fees: {
        fee: authTxn.fee,
        commission: authTxn.commission,
      },
      message: this._message(),
      authenticationData: authData,
      withdrawalId,
    });
  }

  // ── Pipelines ─────────────────────────────────────────────────────────────

  private wireMethodsPipeline(): void {
    this.flow.wire(this.methodsTrigger$, (amount) => this.withdrawalService.getMethods(amount), {
      next: (methods) => {
        // Keep Carte at the end, consistent with the Topup flow
        const sorted = [...methods].sort((a, b) => {
          if (a.name === 'Carte') return 1;
          if (b.name === 'Carte') return -1;
          return 0;
        });
        this._methods.set(sorted);
        this._methodsLoading.set(false);
      },
      // Withdrawal routes method-fetch errors into the shared `error` signal
      // (it exposes no separate methodsError).
      error: (err) => {
        this._error.set((err as Error).message);
        this._methodsLoading.set(false);
      },
    });
  }

  private wireTokensPipeline(): void {
    this.flow.wire(
      this.tokensTrigger$,
      (processorId) => this.withdrawalService.getTokens(processorId),
      {
        next: (tokens) => {
          this._tokens.set(tokens);
          this._tokensLoading.set(false);
        },
        error: (err) => {
          this._tokensError.set((err as Error).message);
          this._tokensLoading.set(false);
        },
      },
    );
  }

  private wireAuthTxnPipeline(): void {
    this.authTxnTrigger$
      .pipe(
        // defer: a synchronous throw from the service/API layer would otherwise escape this
        // projection, bypass the catchError below and kill the subscription permanently.
        switchMap(({ amount, processorId, tokenId, methodId }) =>
          defer(() => this.withdrawalService.authTxn(amount, processorId, tokenId, methodId)).pipe(
            catchError((err: Error) => {
              this._error.set(err.message);
              this._authTxnLoading.set(false);
              return EMPTY;
            }),
          ),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((result) => {
        this._authTxnResult.set(result);
        this._categories.set(result.categories);
        // Category is intentionally NOT pre-selected — the user decides.
        // Store withdrawal_id captured from the auth_txn response —
        // it is a required field for both verify_pin and init_psp_withdraw.
        this._withdrawalId.set(result.withdrawal_id);
        this._authTxnLoading.set(false);
      });
  }

  private wireVerifyPinPipeline(): void {
    this.verifyPinTrigger$
      .pipe(
        // defer: see wireAuthTxnPipeline. Critical here — a lost subscription would leave
        // the PIN keypad permanently unresponsive.
        switchMap(({ rawPin, withdrawalId }) =>
          defer(() => this.withdrawalService.verifyPin(rawPin, withdrawalId)).pipe(
            // verifyPin returns { success: true } — the PIN itself is the security
            // credential forwarded to init_psp_withdraw as authentication_data.
            map(() => rawPin),
            catchError((err: unknown) => {
              this.pinStore.recordFailure(err);
              this._verifyPinLoading.set(false);
              return EMPTY;
            }),
          ),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((pin) => {
        // PIN verified — store the raw PIN as authentication_data and submit
        this._authData.set(pin);
        this.pinStore.reset();
        this._verifyPinLoading.set(false);
        this.dispatchSubmit();
      });
  }

  private wireSubmitPipeline(): void {
    this.submitTrigger$
      .pipe(
        // defer: see wireAuthTxnPipeline.
        switchMap((params) =>
          defer(() => this.withdrawalService.initWithdrawal(params)).pipe(
            catchError((err: Error) => {
              // HTTP-level failure (network error, 4xx, 5xx) — render failure phase
              this._failureError.set(err.message);
              this._failureTransactionId.set(null);
              this._failureErrorCode.set(null);
              this._submitting.set(false);
              this._dialogStep.set('failure');
              return EMPTY;
            }),
          ),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((result) => {
        this._submitting.set(false);

        // Success criterion: HTTP 200 received (we're inside subscribe, not catchError)
        // AND payment_id is a non-empty string.
        // The `success` boolean is intentionally ignored — it is unreliable.
        const isSuccess =
          typeof result.payment_id === 'string' && result.payment_id.trim().length > 0;

        if (!isSuccess) {
          // Business failure — payment_id absent or empty means the backend did not
          // create a payment record. Populate failure signals and show failure phase.
          this._failureError.set(
            result.message ??
              result.error_message ??
              `Erreur ${result.error_code ?? ''} — Le retrait a échoué.`.trim(),
          );
          this._failureTransactionId.set(
            result.transaction_unique_id == null ? null : String(result.transaction_unique_id),
          );
          this._failureErrorCode.set(result.error_code ?? null);
          this._dialogStep.set('failure');
          return;
        }

        // Genuine success — assemble the full WithdrawalSuccessState so the parent
        // WalletPage can show the success banner without querying the store separately.
        // All reads are wrapped in untracked() since this runs outside the signal graph.
        const successState: WithdrawalSuccessState = untracked(() => ({
          result,
          methodName: this._selectedMethod()?.name ?? '',
          tokenDisplay: this._selectedToken()?.display_key ?? '',
          currency: this._authTxnResult()?.currency ?? '',
          amount: this._amount() ?? 0,
        }));

        this._lastWithdrawalResult.set(successState);
        this._txDetailLoading.set(true);
        this._txStatus.set(null);
        this._detailTrigger$.next(result.payment_id);
      });
  }

  private wireDetailPipeline(): void {
    this._detailTrigger$
      .pipe(
        switchMap((paymentId) =>
          this.transactionStatus.loadStatus(paymentId).pipe(
            // Reset the loading flag when the request settles — on success, on
            // an empty result, and (the fix) on error. `loadStatus` swallows
            // errors into EMPTY, so the stream completes without emitting; the
            // old code cleared the flag only in the `next` handler, leaving
            // txDetailLoading stuck `true` after a failed status fetch.
            finalize(() => this._txDetailLoading.set(false)),
          ),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((status) => {
        this._txStatus.set(status);
      });
  }

  private wireAddTokenPipeline(): void {
    this.flow.wire(
      this.addTokenTrigger$,
      (form) => {
        const method = this._selectedMethod();
        if (!method) {
          this._addTokenError.set('Aucune méthode sélectionnée.');
          this._addTokenLoading.set(false);
          return EMPTY;
        }
        // tokenService = 2 → WITHDRAWAL tokens only
        return this.addTokenService.createToken(form, method, 2);
      },
      {
        next: (newToken) => {
          // Prepend the new token to the list and auto-select it
          this._tokens.update((prev) => [newToken, ...prev]);
          this._addTokenLoading.set(false);
          this._addAccountMode.set(false);
          this.selectToken(newToken);
        },
        error: (err) => {
          this._addTokenError.set((err as Error).message);
          this._addTokenLoading.set(false);
        },
      },
    );
  }
}
